import React, { Component, ReactNode } from "react";
import { SecondaryButton } from "@/components/StandardButton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, RefreshCcw } from "lucide-react";
import { logger } from "@/utils/logger";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class AdminErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error("Admin page error:", { error, errorInfo });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex items-center justify-center min-h-[400px] p-4">
          <Alert variant="destructive" className="max-w-lg">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Something went wrong</AlertTitle>
            <AlertDescription className="mt-2 space-y-4">
              <p>We encountered an error loading this admin page.</p>
              {this.state.error && (
                <p className="text-xs opacity-70 font-mono">
                  {this.state.error.message}
                </p>
              )}
              <SecondaryButton onClick={this.handleRetry} size="sm">
                <RefreshCcw className="h-4 w-4 mr-2" />
                Try Again
              </SecondaryButton>
            </AlertDescription>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}
